import { useCallback, useMemo } from 'react'
import classnames from 'classnames'
import { Order, PageLoadingSpinner, SecondaryButton } from 'ui_kit_qmd'
import {
  ADD_TEXT,
  NO_PRESCRIPTIONS_TEXT,
  VIDEO_CALL_NOTES_PRESCRIPTIONS_TITLE_TEXT,
  VISIBLE_TO_PATIENT_IN_VISIT_SUMMARY_TEXT,
} from '../../../../utils/constants'
import {
  useGetVisitsVisitIdEncounterUrl,
  useGetVisitsVisitIdPrescriptions,
} from '../../../../api/generated/prescription/prescription'
import { mapPrescriptionToLegacy } from '../../../../utils/prescriptionMapper'
import { useCanCreatePrescriptionEncounter } from '../../../../hooks/useCanCreatePrescriptionEncounter'
import plusIcon from '../../../../images/management/clinicMembers/plusIcon.png'
import './PrescriptionsSection.scss'

const addBtnContainerStyle = {
  justifyContent: 'center',
  maxHeight: 40,
  width: 88,
}

const addBtnImageStyle = {
  height: 16.5,
  width: 16.5,
  marginRight: 5,
}

type Props = {
  visitId: string
  onSetPrescription: (prescription: ReturnType<typeof mapPrescriptionToLegacy>) => void
  isUpcomingVisit?: boolean
}

export const PrescriptionsSection = ({ visitId, onSetPrescription, isUpcomingVisit }: Props) => {
  const { data: rawPrescriptions = [], isLoading } = useGetVisitsVisitIdPrescriptions(visitId, {
    query: { enabled: !!visitId },
  })

  const prescriptions = useMemo(() => rawPrescriptions.map(mapPrescriptionToLegacy), [rawPrescriptions])

  const canCreateEncounter = useCanCreatePrescriptionEncounter()
  const showAddButton = !isUpcomingVisit && canCreateEncounter

  // Fetched on demand (button click), not on mount.
  const { refetch: fetchEncounterUrl, isFetching: isEncounterUrlLoading } = useGetVisitsVisitIdEncounterUrl(visitId, {
    query: { enabled: false },
  })

  const handleAddPrescription = useCallback(async () => {
    const { data } = await fetchEncounterUrl()
    if (data?.ProfileUrl) window.open(data.ProfileUrl, '_blank', 'noopener noreferrer')
  }, [fetchEncounterUrl])

  return (
    <>
      <div
        className={classnames('prescriptions-section-header', {
          'prescriptions-section-header--without-btn': !showAddButton,
        })}>
        <div className='prescriptions-section-text prescriptions-section-title'>
          {VIDEO_CALL_NOTES_PRESCRIPTIONS_TITLE_TEXT}
        </div>
        {showAddButton ?
          <SecondaryButton
            id='prescriptions-section-add-button'
            image={plusIcon}
            imageStyle={addBtnImageStyle}
            btnText={ADD_TEXT}
            onPress={handleAddPrescription}
            disabled={isEncounterUrlLoading}
            containerStyle={addBtnContainerStyle}
          />
        : null}
      </div>
      <div className='prescriptions-section-text prescriptions-section-description'>
        {VISIBLE_TO_PATIENT_IN_VISIT_SUMMARY_TEXT}
      </div>
      {isLoading ?
        <PageLoadingSpinner />
      : prescriptions.length ?
        <div className='prescriptions-section-content-list-wrapper'>
          {prescriptions.map((item) => (
            <Order
              key={item.PrescriptionId}
              onEdit={() => {}}
              onDelete={() => {}}
              onOpenOrder={() => {
                onSetPrescription({ ...item })
              }}
              order={{ ...item }}
            />
          ))}
        </div>
      : <div className='prescriptions-section-text prescriptions-section-no-prescriptions-tile'>
          {NO_PRESCRIPTIONS_TEXT}
        </div>
      }
      <div className='prescriptions-section-separator' />
    </>
  )
}
